import { useMemo, useState } from "react";
import { useAnalyticsOrders, type Order } from "../queries/ordersQueries";
import { useMenuItems } from "../queries/menuQueries";
import { formatPrice } from "../utils/currency";
import {
  Container,
  Title,
  Table,
  Text,
  Group,
  Pagination,
  Center,
  Loader,
  Alert,
  Paper,
  TextInput,
  Stack,
  Badge,
  Anchor,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";

const ITEMS_PER_PAGE = 30;

const formatPhoneNumber = (value?: string | null) => {
  if (!value) return "-";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 8) {
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(
      4,
      6
    )} ${digits.slice(6)}`;
  }
  return value;
};

const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString("da-DK", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const getStatusColor = (status: Order["status"]) => {
  switch (status) {
    case "pending":
      return "yellow";
    case "confirmed":
      return "blue";
    case "preparing":
      return "orange";
    case "ready":
      return "teal";
    case "completed":
      return "green";
    case "cancelled":
      return "red";
    default:
      return "gray";
  }
};

export default function AnalyticsPage() {
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const offset = (page - 1) * ITEMS_PER_PAGE;

  const {
    data: analyticsData,
    isLoading,
    error,
  } = useAnalyticsOrders({
    limit: ITEMS_PER_PAGE,
    offset,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const { data: menuItems } = useMenuItems(false);

  const menuById = useMemo(() => {
    const map: Record<string, { name: string; menu_number?: string }> = {};
    (menuItems || []).forEach((m) => {
      map[m.id] = { name: m.name, menu_number: m.menu_number || undefined };
    });
    return map;
  }, [menuItems]);

  const totalPages = analyticsData
    ? Math.ceil(analyticsData.total / ITEMS_PER_PAGE)
    : 0;

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFrom(e.target.value);
    setPage(1);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTo(e.target.value);
    setPage(1);
  };

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Fejl">
          Kunne ikke hente ordredata. Prøv igen senere.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Title order={1} mb="lg">
        Analytics
      </Title>

      <Paper withBorder p="md" mb="lg">
        <Group gap="md" align="flex-end">
          {/* <IconFilter size={20} /> */}
          <TextInput
            label="Fra dato"
            type="date"
            value={dateFrom}
            onChange={handleDateFromChange}
            style={{ width: 180 }}
          />
          <TextInput
            label="Til dato"
            type="date"
            value={dateTo}
            onChange={handleDateToChange}
            style={{ width: 180 }}
          />
          <Text size="sm" c="dimmed">
            Viser {analyticsData?.orders.length ?? 0} af{" "}
            {analyticsData?.total ?? 0} ordrer
          </Text>
        </Group>
      </Paper>

      {!analyticsData?.orders.length ? (
        <Paper withBorder p="xl">
          <Text ta="center" c="dimmed">
            Ingen ordrer fundet
          </Text>
        </Paper>
      ) : (
        <>
          <Paper withBorder style={{ overflowX: "auto" }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Ordre ID</Table.Th>
                  <Table.Th>Dato/Tid</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Varer</Table.Th>
                  <Table.Th>Total</Table.Th>
                  <Table.Th>Telefon</Table.Th>
                  <Table.Th>Kommentar</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {analyticsData.orders.map((order) => (
                  <Table.Tr key={order.id}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {order.id.slice(0, 8)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDateTime(order.created_on)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(order.status)} size="sm">
                        {order.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        {order.items.map((item) => {
                          const menuItem = menuById[item.menu_item_id];
                          return (
                            <Text key={item.id} size="sm">
                              {item.quantity}x {menuItem?.name || "Ukendt vare"}
                              {menuItem?.menu_number && (
                                <Text span c="dimmed" size="xs">
                                  {" "}
                                  (#{menuItem.menu_number})
                                </Text>
                              )}
                            </Text>
                          );
                        })}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {formatPrice(order.total_amount)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {order.guest_phone ? (
                        <Anchor href={`tel:${order.guest_phone}`} size="sm">
                          {formatPhoneNumber(order.guest_phone)}
                        </Anchor>
                      ) : (
                        <Text size="sm" c="dimmed">
                          -
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 200 }}>
                      <Text size="sm" lineClamp={2}>
                        {order.notes || "-"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>

          {totalPages > 1 && (
            <Center mt="lg">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Center>
          )}
        </>
      )}
    </Container>
  );
}
